'use client';

import React from 'react';
import { ExternalLink, Mail } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { BrandText } from '@/components/marketing/BrandText';
import { PartnerLogo } from '@/components/marketing/PartnerLogo';
import { MARKETING_PARTNERS } from '@/lib/marketing/partners';
import { partnerLinkRel, resolvePartnerLinks } from '@/lib/marketing/resolvePartnerLinks';

export default function PartnersSection() {
  const { t, lang } = useLanguage();

  return (
    <section
      className="bg-slate-50 border-y border-slate-200/90 py-16 lg:py-20"
      aria-labelledby="partners-heading"
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 lg:mb-12">
          <p className="text-[11px] font-semibold text-[#B8956A] uppercase tracking-widest mb-2">{t('partners.tag')}</p>
          <h2
            id="partners-heading"
            className="font-display text-2xl sm:text-3xl font-bold text-[#1B3A5C] tracking-tight mb-3"
          >
            {t('partners.title')}
          </h2>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed">{t('partners.desc')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
          {MARKETING_PARTNERS.map((p) => (
            <article
              key={p.id}
              className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 flex flex-col gap-3 shadow-sm hover:shadow-md hover:border-slate-300/90 transition-shadow duration-200"
            >
              <div className="flex items-start gap-3">
                <PartnerLogo
                  domain={p.faviconDomain}
                  name={t(p.nameKey)}
                  logoSrc={p.logoSrc}
                  logoWide={p.logoWide}
                />
                <div className="min-w-0 flex-1">
                  <h3 className="font-display font-semibold text-[#1B3A5C] text-base leading-snug">{t(p.nameKey)}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mt-2">
                    <BrandText text={t(p.descKey)} />
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1 text-xs">
                {resolvePartnerLinks(p, lang).map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel={partnerLinkRel(p)}
                    className="inline-flex items-center gap-1 text-[#1B3A5C] hover:text-[#14304d] font-medium transition-colors"
                  >
                    <ExternalLink className="size-3.5 opacity-70" aria-hidden />
                    {link.label}
                  </a>
                ))}
                {p.emails?.map((em) => (
                  <a
                    key={em.href}
                    href={em.href}
                    className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    <Mail className="size-3.5 opacity-70" aria-hidden />
                    {em.label}
                  </a>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
